import { useSessionContext, useSessionMessages } from "@livekit/components-react";

// Send a typed line. Normally a session is already live (opening the panel
// starts it). If it isn't — the first start failed, or the ended screen's
// "new chat" remounted us with a message — the send IS the session start:
// record it as the room's initial message (the agent then answers it directly
// instead of opening with the generic greeting), connect, then deliver it.
// Errors propagate so the composer can restore the user's text.
export async function sendOrStart({
  text,
  sessionStarted,
  ensureSession,
  setInitialMessage,
  send,
}: {
  text: string;
  sessionStarted: boolean;
  ensureSession: (withMic: boolean) => Promise<void>;
  setInitialMessage?: (msg: string) => void;
  send: (text: string) => Promise<unknown>;
}) {
  if (!sessionStarted) {
    setInitialMessage?.(text);
    await ensureSession(false);
  }
  await send(text);
}

export function useChatTransport({
  sessionStarted,
  ensureSession,
  reportError,
  setInitialMessage,
}: {
  sessionStarted: boolean;
  ensureSession: (withMic: boolean) => Promise<void>;
  reportError: (message: string | null) => void;
  setInitialMessage?: (msg: string) => void;
}) {
  const session = useSessionContext();
  const { messages, send, isSending } = useSessionMessages();

  const sendMessage = async (text: string) => {
    reportError(null);
    try {
      await sendOrStart({ text, sessionStarted, ensureSession, setInitialMessage, send });
    } catch (err) {
      console.error("[lk-widget] send failed:", err);
      const msg = err instanceof Error ? err.message : String(err);
      reportError(`Couldn't send: ${msg || "unknown error"}`);
      throw err; // surface to MessageInput so it restores its input
    }
  };

  // Open the conversation with no first message: the agent greets on its own.
  const startChat = async () => {
    if (sessionStarted) return;
    reportError(null);
    try {
      await ensureSession(false);
    } catch (err) {
      console.error("[lk-widget] startChat failed:", err);
      const msg = err instanceof Error ? err.message : String(err);
      reportError(`Couldn't start chat: ${msg || "unknown error"}`);
    }
  };

  const startChatWithMessage = async (text: string) => {
    reportError(null);
    try {
      await sendOrStart({ text, sessionStarted, ensureSession, setInitialMessage, send });
    } catch (err) {
      console.error("[lk-widget] startChatWithMessage failed:", err);
      const msg = err instanceof Error ? err.message : String(err);
      reportError(`Couldn't start chat: ${msg || "unknown error"}`);
    }
  };

  const showConnecting =
    sessionStarted && messages.length === 0 && session.connectionState !== "connected";

  return {
    state: { messages, isSending, showConnecting },
    actions: { sendMessage, startChat, startChatWithMessage },
  };
}
